/**
 * CompareStore — singleton data layer for the compare feature.
 *
 * Persists product handles in localStorage and dispatches
 * custom events for reactivity across components.
 *
 * @example
 *   import { CompareStore } from '@theme/compare-store';
 *   CompareStore.toggle('jbl-charge-5');
 *   console.log(CompareStore.getAll()); // ['jbl-charge-5', ...]
 */

const STORAGE_KEY = 'plugs_go_compare';
const MAX_PRODUCTS = 4;

/**
 * Read the current handle list from localStorage.
 * Always returns a clean array, never null.
 * @returns {string[]}
 */
function getStoredHandles() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Persist handles to localStorage and dispatch the update event.
 * @param {string[]} handles
 */
function setStoredHandles(handles) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  window.dispatchEvent(
    new CustomEvent('compare:updated', {
      detail: { handles, count: handles.length },
      bubbles: false,
    })
  );
}

export const CompareStore = {
  /** @returns {string[]} */
  getAll() {
    return getStoredHandles();
  },

  /** @returns {number} */
  count() {
    return getStoredHandles().length;
  },

  /**
   * @param {string} handle
   * @returns {boolean}
   */
  has(handle) {
    return getStoredHandles().includes(handle);
  },

  /**
   * Add a handle. Silently ignored if already present or at max.
   * When max is reached, dispatches `compare:max-reached`.
   * @param {string} handle
   */
  add(handle) {
    const handles = getStoredHandles();
    if (handles.includes(handle)) return;
    if (handles.length >= MAX_PRODUCTS) {
      window.dispatchEvent(
        new CustomEvent('compare:max-reached', {
          detail: { max: MAX_PRODUCTS },
          bubbles: false,
        })
      );
      return;
    }
    handles.push(handle);
    setStoredHandles(handles);
  },

  /**
   * Remove a handle. Silently ignored if not present.
   * @param {string} handle
   */
  remove(handle) {
    const handles = getStoredHandles().filter((h) => h !== handle);
    setStoredHandles(handles);
  },

  /**
   * Toggle a handle on/off.
   * @param {string} handle
   * @returns {boolean} `true` if now selected, `false` if now deselected
   */
  toggle(handle) {
    if (this.has(handle)) {
      this.remove(handle);
      return false;
    }
    this.add(handle);
    return true;
  },

  /** Remove all handles. */
  clear() {
    setStoredHandles([]);
  },
};
